import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";

import {
  CardBody,
  CardGroup,
  CardRow,
  CardWrapper,
  CardTitle,
  SearchInput,
  INPUT_SIZES,
  MenuListItem,
  IconParts,
  IconAccounting,
  IconDealerShowroom,
  IconCashRegister,
  IconAssignment,
  ListItem,
  ListItemText,
  ListItemPrimaryText,
  ListItemCheckbox,
  Loader,
  withTooltip,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS,
  THEMES
} from 'cdk-radial';
import {
  clearDMSAccountInfo,
  getDMSAccountDetails,
  setDmsLocations,
  setDmsDepartments,
  handleLocationSelection,
  handleDepartmentSelection,
  handleDMSAccountSelection,
  handleDMSAccountSelectionAll,
  handleRemoveLocation,
} from '../../../../../../../redux/connectedComponent/ManageIntegrationsV2/ManageIntegrationsV2.slice';
import useDebounce from '../../../../../../../hooks/debounce';

const CustomSubHeader = ({ header }) => {
  return (
    <h4
      className='dms-step-custom-header'
    >
      {header}
    </h4>
  );
};

const DepartmentsNameAndIconData = {
    'Parts': {
      accountType: 'Part DMS Account',
      icon: <IconParts />,
    },
    'Finance': {
      accountType: 'Finance DMS Account',
      icon: <IconAccounting />,
    },
    'Service': {
      accountType: 'Service DMS Account',
      icon: <IconDealerShowroom />,
    },
    'Accounting': {
      accountType: 'Account DMS Account',
      icon: <IconCashRegister />,
    },
    'Inventory': {
      accountType: 'Inventory DMS Account',
      icon: <IconAssignment />,
    },
  };

const DMSContainer = ({apiDetails, subscriptionData}) => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState('');
  const [dealershipLocations, setDealershipLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [dmsAccounts, setDmsAccounts] = useState([]);
  const {apiDisplayName, apiId: restApiId, id: providerId} = apiDetails;

  // we want the api id without a leading api-v2-.
  // apiId is the correct field for rest providers; async ones don't have apiId,
  // so use id instead
  const apiId = restApiId ?? providerId;

  const state = useSelector((state) => state);
  const { authenticationToken } = state.loginDetailsSlice;
  const { dmsLocations, isEISDMSInfoSuccess, dmsLocationSpecificAccounts } = state.ManageIntegrationsV2Slice;
  const {ManageIntegrationsV2Slice, loginDetailsSlice: {entityIdNameMapping}, ConfigurationsSlice: {dmsAccountsSupport}} = state;
  const handleLocationSearch = (searchValue) => {
    setSearchValue(searchValue);
  };

  const handleLocationSelectionLocal = (event) => {
    dispatch(handleLocationSelection(event));
  }

  const handleDepartmentSelectionLocal = (event) => {
    dispatch(handleDepartmentSelection(event))
  }

  const handleDMSAccountSelectionLocal = (event) => {
    if(event.isVisited){ // uncheck
      dispatch(handleDMSAccountSelection({check: false, event}));
    } else { // check
      dispatch(handleDMSAccountSelection({check: true, event}));
    }
  }

  const handleDMSAccountSelectionAllLocal = (event) => {
    if(event){
      dispatch(handleDMSAccountSelectionAll(false));
    } else {
      dispatch(handleDMSAccountSelectionAll(true));
    }
  }

  const LocationNameWithToolTip = (option, ind) => {
    const tooltipProps = {
      id: `tooltip-id-${ind}`,
      text: option.location,
      theme: THEMES.LIGHT,
    };

    const ComponentToRender = (props) => {
      return (
        <span {...props}>
          <MenuListItem
            dataTestId={`menu-location-${ind}`}
            onClick={() => handleLocationSelectionLocal(option)}
            isSelected={option.isVisited}
            tabIndex={ind}
          >
            {option.location}
          </MenuListItem>
        </span>
      );
    }

    const ComponentWithTooltip = withTooltip(ComponentToRender, tooltipProps);

    const ToolTip = () => {
      return (
          <ComponentWithTooltip
            horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.LEFT}
            marginAroundElement={8}
            verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.RIGHT}
          />
      );
    };

    return <ToolTip />
  }

  useEffect(() => {
    let selectedLocations = ManageIntegrationsV2Slice.requestedIntegrationStatuses;
    let dealerShipData = [];
    Object.keys(selectedLocations).forEach((entityId) => {
      if(selectedLocations[entityId] === 'enable'){
        dealerShipData.push({
          locationName: entityIdNameMapping[entityId],
          entityId,
          isSelected: false
        })
      }
    })
    dispatch(setDmsLocations(dealerShipData));
    const departmentData = dmsAccountsSupport[apiId]?.map((dep) => {
      return {
        name: dep,
        accountType: DepartmentsNameAndIconData[dep].accountType
      }
    });
    dispatch(setDmsDepartments(departmentData));
  }, [])

  useEffect(() => {
    if (searchValue === '') {
      setDealershipLocations(dmsLocationSpecificAccounts);
      let depData = dmsLocationSpecificAccounts?.find(e => e.isVisited)?.departments || [];
      setDepartments(depData);
      let accountsData = depData?.find(e => e.isVisited)?.accounts || [];
      setDmsAccounts(accountsData);
    } else {
      let filteredLocations
      let searchString = searchValue.toLowerCase();
      filteredLocations = dmsLocationSpecificAccounts.filter((dealership) => dealership.location.toLowerCase().includes(searchString));
      setDealershipLocations(filteredLocations);
      let depData = filteredLocations?.find(e => e.isVisited)?.departments || [];
      setDepartments(depData);
      let accountsData = depData?.find(e => e.isVisited)?.accounts || [];
      setDmsAccounts(accountsData);
    }
  }, [searchValue, dmsLocationSpecificAccounts]);

  useDebounce(100, () => {
    let selectedLocationCmf = [];
    let removedSelectedLocationData = [];
    let addedLocationData = [];
    dmsLocations.forEach((selectedLocation)=> {
      selectedLocationCmf.push({
        location: selectedLocation.locationName,
        ...subscriptionData.find(subscribedLocations => selectedLocation.entityId === subscribedLocations.entityId)
      });
    });

    let removedLocation = [];
    let addedLocation = [];
    selectedLocationCmf.forEach(e => {
      if (!dmsLocationSpecificAccounts.some((a) => a.cmfNumber === e.entitycmfNumber)) {
        addedLocation.push(e);
      }
    });
    dmsLocationSpecificAccounts.forEach(e => {
      if(!selectedLocationCmf.some((a)=> a.entitycmfNumber === e.cmfNumber)){
        removedLocation.push(e)
      }
    });
    addedLocationData = [...addedLocation];
    removedSelectedLocationData = [...removedLocation];

    if (removedSelectedLocationData.length > 0) {
      dispatch(handleRemoveLocation(removedSelectedLocationData));
    }
    if (addedLocationData.length > 0) {
      dispatch(clearDMSAccountInfo());
    }
    addedLocationData.forEach((locationData) => {
      let locationCMF = locationData.entitycmfNumber;
      if (!locationData.entitycmfNumber) {
        locationCMF = state.loginDetailsSlice.canPurchaseEnabled.find(e => e.entityId === locationData.entityId).cmfNumber
      }
      dispatch(
        getDMSAccountDetails({
          location: locationData.location,
          cmfNumber: locationCMF,
          storeId: locationData.storeId,
          entityId: locationData.entityId,
          token: authenticationToken,
          dmsApiDetails: apiDetails,
        })
      );
     });
  }, [dmsLocations])

  return (
    <>
      {!isEISDMSInfoSuccess ? (
        <Loader className="dms_loader" label={'Loading DMS Accounts ...'} />
      ) : (
        <CardWrapper className="dms_step_card_wrapper">
          <CardBody className="dms_step_card_body">
            <CardTitle className="dms_step_card_title">
              {apiDisplayName}
            </CardTitle>
            <div className="dms_step_card_divider" />
            <CardRow className="dms_step_card_row">
              <CardGroup className="dms_card_group vertical_divider">
                <CustomSubHeader header={'Dealership Locations'} />
                <div className="dms_card_location_group_container">
                  <SearchInput
                    clearButtonLabel="clearButtonLabel"
                    id="search-input-expanded"
                    isAlwaysExpanded
                    name="search-input-expanded"
                    onChange={handleLocationSearch}
                    placeholder="Search Location"
                    size={INPUT_SIZES.STANDARD}
                    value={searchValue}
                  />
                </div>
                <div className="dms_card_location_group_list">
                  {dealershipLocations.map((option, ind) => {
                    return (
                      <>
                        {LocationNameWithToolTip(option, ind)}
                      </>
                    );
                  })}
                </div>
              </CardGroup>
              <CardGroup className="dms_card_group vertical_divider">
                <CustomSubHeader header={'Departments'} />
                <div className="dms_card_department_list">
                  {departments.map((option, ind) => {
                    return (
                      <MenuListItem
                        dataTestId={`menu-department-${ind}`}
                        onClick={() => handleDepartmentSelectionLocal(option)}
                        isSelected={option.isVisited}
                        tabIndex={ind}
                      >
                        <div className="dms_card_menue_item">
                          <span className="dms_card_menue_item_span">
                            {DepartmentsNameAndIconData[option.name].icon}
                          </span>
                          <span>
                            {DepartmentsNameAndIconData[option.name].accountType}
                          </span>
                        </div>
                      </MenuListItem>
                    );
                  })}
                </div>
              </CardGroup>
              <CardGroup className="dms_card_group">
                <CustomSubHeader header={'DMS Accounts'} />
                <div>
                  <ListItem
                    dataTestId='select-all-item'
                    onClick={() =>
                      handleDMSAccountSelectionAllLocal(
                        departments.find((e) => e.isVisited).isSelectAll
                      )
                    }
                  >
                    <ListItemCheckbox
                      checked={
                        departments?.find((e) => e.isVisited)?.isSelectAll ||
                        departments
                          ?.find((e) => e.isVisited)
                          ?.accounts?.some((e) => e.isVisited)
                      }
                      indeterminate={
                        departments?.find((e) => e.isVisited)?.isSelectAll
                          ? false
                          : true
                      }
                    />
                    <ListItemText>
                      <ListItemPrimaryText>Select All</ListItemPrimaryText>
                    </ListItemText>
                  </ListItem>
                  <div className="dms_accounts_divider " />
                  <div className='dms_list_container'>
                    {dmsAccounts.map((option, ind) => {
                      return (
                        <ListItem
                          dataTestId={`dms-account-${option.name}`}
                          isSelected={true}
                          onClick={() => handleDMSAccountSelectionLocal(option)}
                        >
                          <ListItemCheckbox checked={option.isVisited} />
                          <ListItemText>
                            <ListItemPrimaryText>
                              {option.name}
                            </ListItemPrimaryText>
                          </ListItemText>
                        </ListItem>
                      );
                    })}
                  </div>
                </div>
              </CardGroup>
            </CardRow>
          </CardBody>
        </CardWrapper>
      )}
    </>
  );
};

export default DMSContainer;
